import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardCheck, Download, Plus, Trash2 } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  BASU_CHECKLIST_SECTIONS,
  BASU_CHECKLIST_STATUS_LABELS,
  BASU_CHECKLIST_TEMPLATE,
} from "@/lib/basProcess/basuChecklist";
import { generateBasuChecklistWordDocument } from "@/lib/documentGenerator/basuWordGenerators";

interface Item {
  id: string;
  section: string;
  activity: string;
  requirement: string | null;
  method: string | null;
  document_ref: string | null;
  frequency: string | null;
  responsible: string | null;
  status: string;
  note: string | null;
  due_date: string | null;
  sort_order: number;
}

export const BasuChecklist: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data, error }, { data: proj }] = await Promise.all([
      supabase
        .from("basu_checklist_items")
        .select("*")
        .eq("project_id", projectId)
        .order("sort_order", { ascending: true }),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    if (error) toast.error("Kunde inte läsa checklistan");
    setProjectName(proj?.name ?? "");
    setItems((data as unknown as Item[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const seed = async () => {
    setBusy(true);
    const rows = BASU_CHECKLIST_TEMPLATE.map((t, i) => ({
      project_id: projectId,
      section: t.section,
      activity: t.activity,
      requirement: t.requirement,
      method: t.method,
      document_ref: t.documentRef,
      frequency: t.frequency,
      sort_order: i,
    }));
    const { error } = await supabase.from("basu_checklist_items").insert(rows);
    setBusy(false);
    if (error) return toast.error("Kunde inte skapa checklistan");
    toast.success("Checklistan skapad med standardpunkter");
    load();
  };

  const addRow = async (section: string) => {
    const { error } = await supabase.from("basu_checklist_items").insert({
      project_id: projectId,
      section,
      activity: "Ny kontrollpunkt",
      sort_order: items.length + 1,
    });
    if (error) return toast.error("Kunde inte lägga till punkten");
    load();
  };

  const update = async (id: string, patch: Partial<Item>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    const { error } = await supabase.from("basu_checklist_items").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("basu_checklist_items").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort punkten");
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const done = useMemo(
    () => items.filter((i) => i.status === "klar" || i.status === "ej_aktuell").length,
    [items]
  );
  const progress = items.length ? Math.round((done / items.length) * 100) : 0;
  const deviations = items.filter((i) => i.status === "avvikelse").length;

  const exportWord = async () => {
    try {
      await generateBasuChecklistWordDocument(items, projectName);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar checklistan...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="41"
        exportHistory={{ documentType: "basu_checklista" }}
        title="Checklista Bas-U"
        description="Kontrollpunkter genom hela utförandet – från övertagande till överlämning. Visar vad som är klart, pågående och avvikande."
        legalRef="AFS 2023:3 kap. 4 · AFS 2023:13 kap. 5"
        icon={ClipboardCheck}
        meta={
          items.length ? (
            <div className="flex flex-wrap items-center gap-4">
              <Badge variant={deviations ? "destructive" : "outline"}>
                {deviations ? `${deviations} avvikelser` : "Inga avvikelser"}
              </Badge>
              <div className="w-40">
                <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                  <span>Avklarat</span>
                  <span>
                    {done}/{items.length}
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </div>
          ) : null
        }
        actions={
          items.length ? (
            <Button size="sm" variant="outline" onClick={exportWord}>
              <Download className="mr-1 h-4 w-4" /> Word
            </Button>
          ) : null
        }
      />

      {!items.length ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <ClipboardCheck className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mx-auto max-w-md text-sm text-muted-foreground">
              Checklistan är inte skapad. Skapa den med standardpunkterna för Bas-U och lägg till egna
              punkter vid behov.
            </p>
            <Button onClick={seed} disabled={busy}>
              {busy ? "Skapar..." : "Skapa checklista"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        BASU_CHECKLIST_SECTIONS.map((section) => {
          const rows = items.filter((i) => i.section === section.key);
          return (
            <Card key={section.key}>
              <CardContent className="space-y-3 pt-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold">{section.label}</h2>
                  <Button size="sm" variant="ghost" onClick={() => addRow(section.key)}>
                    <Plus className="mr-1 h-4 w-4" /> Lägg till punkt
                  </Button>
                </div>
                {rows.map((row) => (
                  <div key={row.id} className="space-y-2 rounded-lg border p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <Input
                        key={`a-${row.id}`}
                        className="max-w-md font-medium"
                        defaultValue={row.activity}
                        onBlur={(e) => update(row.id, { activity: e.target.value })}
                      />
                      <div className="flex items-center gap-2">
                        <Select
                          value={row.status}
                          onValueChange={(v) => update(row.id, { status: v })}
                        >
                          <SelectTrigger className="w-44">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(BASU_CHECKLIST_STATUS_LABELS).map(([k, label]) => (
                              <SelectItem key={k} value={k}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Ta bort punkten"
                          onClick={() => remove(row.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {[row.requirement, row.method, row.document_ref, row.frequency]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-3">
                      <Input
                        key={`r-${row.id}`}
                        placeholder="Ansvarig"
                        defaultValue={row.responsible ?? ""}
                        onBlur={(e) => update(row.id, { responsible: e.target.value })}
                      />
                      <Input
                        key={`d-${row.id}`}
                        type="date"
                        defaultValue={row.due_date ?? ""}
                        onBlur={(e) => update(row.id, { due_date: e.target.value || null })}
                      />
                      <Input
                        key={`k-${row.id}`}
                        placeholder="Kommentar"
                        defaultValue={row.note ?? ""}
                        onBlur={(e) => update(row.id, { note: e.target.value })}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default BasuChecklist;
